import { useEffect, useMemo } from 'react';
import {
  Background,
  BackgroundVariant,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type EdgeTypes,
  type NodeTypes,
} from '@xyflow/react';
import { FallbackNode } from '../funnel-editor/components/FallbackNode';
import { SemanticNode } from '../funnel-editor/nodes/components/SemanticNode';
import { getAllNodeDefinitions } from '../funnel-editor/nodes/registry/node-registry';
import { StandardEdge } from '../funnel-editor/edges/components/StandardEdge';
import { ConditionalEdge } from '../funnel-editor/edges/components/ConditionalEdge';
import { UnknownEdge } from '../funnel-editor/edges/components/UnknownEdge';
import { GRID_SIZE, type EditorEdge, type EditorNode } from '../funnel-editor/domain/editor-graph';
import type { GraphEmphasis } from '@/domain/presentation/emphasis';
import type { PresentationViewport } from '@/domain/presentation/contracts';

const edgeTypes: EdgeTypes = {
  standard: StandardEdge,
  conditional: ConditionalEdge,
  unknown: UnknownEdge,
};

const DIMMED_OPACITY = 0.28;

export interface PresentationCanvasProps {
  nodes: readonly EditorNode[];
  edges: readonly EditorEdge[];
  emphasis: GraphEmphasis;
  defaultViewport?: PresentationViewport;
  onNodeClick?: (nodeId: string) => void;
  onPaneClick?: () => void;
  focusNodeIds?: readonly string[] | null;
  strongEmphasis?: boolean;
  transparentBackground?: boolean;
}

/**
 * SPEC-011 §33/§83/plan §11 — reuses the exact same node/edge renderers as
 * the live editor canvas (SPEC-005/006), just with every interaction flag
 * that would mutate the graph turned off. Dimming (PRES-REQ-012) is applied
 * as an opacity style per node/edge — the underlying components never know
 * they are being viewed read-only.
 */
function PresentationViewportController({
  focusNodeIds,
}: {
  focusNodeIds?: readonly string[] | null;
}) {
  const { fitView } = useReactFlow();
  useEffect(() => {
    if (focusNodeIds === undefined) return;
    void fitView({
      nodes: focusNodeIds?.length ? focusNodeIds.map((id) => ({ id })) : undefined,
      padding: 0.24,
      duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 260,
    });
  }, [fitView, focusNodeIds]);
  return null;
}

export function PresentationCanvas({
  nodes,
  edges,
  emphasis,
  defaultViewport,
  onNodeClick,
  onPaneClick,
  focusNodeIds,
  strongEmphasis = false,
  transparentBackground = false,
}: PresentationCanvasProps) {
  const nodeTypes = useMemo<NodeTypes>(
    () => ({
      fallback: FallbackNode,
      ...Object.fromEntries(getAllNodeDefinitions().map((d) => [d.type, SemanticNode])),
    }),
    [],
  );

  const renderNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        draggable: false,
        selectable: false,
        connectable: false,
        style: {
          ...node.style,
          opacity: emphasis.nodes.get(node.id) === 'dimmed' ? DIMMED_OPACITY : 1,
          filter:
            strongEmphasis && emphasis.nodes.get(node.id) === 'full'
              ? 'drop-shadow(0 0 10px rgba(235, 194, 102, 0.5))'
              : undefined,
        },
      })),
    [nodes, emphasis, strongEmphasis],
  );
  const renderEdges = useMemo(
    () =>
      edges.map((edge) => ({
        ...edge,
        focusable: false,
        style: {
          ...edge.style,
          opacity: emphasis.edges.get(edge.id) === 'dimmed' ? DIMMED_OPACITY : 1,
        },
      })),
    [edges, emphasis],
  );

  return (
    <div
      className={`h-full min-w-0 flex-1 ${transparentBackground ? 'bg-transparent' : 'bg-bg-canvas'}`}
      data-testid="presentation-canvas"
    >
      <ReactFlowProvider>
        <ReactFlow
          nodes={renderNodes}
          edges={renderEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={(_, node) => onNodeClick?.(node.id)}
          onPaneClick={() => onPaneClick?.()}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          edgesFocusable={false}
          deleteKeyCode={null}
          panOnScroll
          zoomOnScroll
          {...(defaultViewport
            ? { defaultViewport }
            : { fitView: true, fitViewOptions: { padding: 0.18 } })}
          minZoom={0.1}
          maxZoom={2}
          snapGrid={[GRID_SIZE, GRID_SIZE]}
          proOptions={{ hideAttribution: true }}
        >
          {transparentBackground ? null : (
            <Background
              variant={BackgroundVariant.Dots}
              gap={GRID_SIZE}
              size={1}
              color="rgba(198, 144, 50, 0.14)"
            />
          )}
          <PresentationViewportController focusNodeIds={focusNodeIds} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}
